import React from 'react';
import { Segment, Header, Message, Divider, Checkbox, Button } from 'semantic-ui-react';
import { toast } from 'react-toastify';

const ApiDocumentationTab = ({ questionnaire, questionnaireToken, tenantToken, onToggleAccess, canUpdate = false }) => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const apiEnv = process.env.REACT_APP_ENV;
    
    const exportEndpoint = `${apiUrl}/${apiEnv}/external-export-questionnaire`;
    const statusEndpoint = `${apiUrl}/${apiEnv}/external-export-status/{exportId}`;

    const exportExample = `curl -X POST "${exportEndpoint}" \\
  -H "X-Tenant-Token: ${tenantToken || '<your-tenant-token>'}" \\
  -H "X-Questionnaire-Token: ${questionnaireToken || '<questionnaire-token>'}" \\
  -H "Content-Type: application/json" \\
  -d '{"startDate": "2025-01-01", "endDate": "2025-12-31"}'`;
    const exportResponse = `{
  "message": "Export initiated",
  "exportId": "2JKLMNOPQRSTUVWXYZabcdef",
  "status": "processing"
}`;

    const statusExample = `curl -X GET "${apiUrl}/${apiEnv}/external-export-status/2JKLMNOPQRSTUVWXYZabcdef" \\
  -H "X-Tenant-Token: ${tenantToken || '<your-tenant-token>'}" \\
  -H "X-Questionnaire-Token: ${questionnaireToken || '<questionnaire-token>'}"`;

    const statusResponse = `{
  "exportId": "2JKLMNOPQRSTUVWXYZabcdef",
  "status": "Export Completed",
  "downloadUrl": "https://s3.amazonaws.com/...",
  "expiresIn": 3600
}`;

    return (
        <Segment>
            <Header as="h3">External API Access</Header>
            <p>Enable external applications to export this questionnaire's data via API.</p>
            
            <Checkbox
                toggle
                label="Enable External API Access"
                checked={questionnaire?.externalApiEnabled || false}
                onChange={(e, { checked }) => onToggleAccess(checked)}
                disabled={!canUpdate}
                style={{ marginBottom: '20px' }}
            />

            {questionnaire?.externalApiEnabled && questionnaireToken && (
                <>
                    <Segment secondary>
                        <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '10px' }}>
                            Questionnaire API Token:
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <code style={{ flex: 1, fontSize: '12px', wordBreak: 'break-all', padding: '10px', background: '#f5f5f5', borderRadius: '4px' }}>
                                {questionnaireToken}
                            </code>
                            <Button
                                icon="copy"
                                primary
                                onClick={() => {
                                    navigator.clipboard.writeText(questionnaireToken);
                                    toast.success('Token copied to clipboard');
                                }}
                            />
                        </div>
                        {questionnaire?.externalApiTokenCreatedAt && (
                            <p style={{ color: '#666', fontSize: '11px', marginTop: '8px' }}>
                                Generated: {new Date(questionnaire.externalApiTokenCreatedAt).toLocaleString()}
                            </p>
                        )}
                    </Segment>

                    <Divider />

                    <Header as="h4">API Documentation</Header>
                    
                    <Message info>
                        <Message.Header>How It Works</Message.Header>
                        <ol style={{ marginTop: '10px' }}>
                            <li>Call the export endpoint to initiate a questionnaire export</li>
                            <li>Receive an <code>exportId</code> in the response</li>
                            <li>Poll the status endpoint with the <code>exportId</code> to check progress</li>
                            <li>When status is "Export Completed", download the CSV from the provided URL</li>
                        </ol>
                    </Message>

                    <Divider />

                    <Header as="h4">1. Initiate Export</Header>
                    <Segment secondary>
                        <code>POST {exportEndpoint}</code>
                    </Segment>

                    <Header as="h5">Required Headers</Header>
                    <Segment secondary>
                        <p><strong>X-Tenant-Token:</strong> Your tenant's long-lived API token (get from Admin → API Token)</p>
                        <p><strong>X-Questionnaire-Token:</strong> The questionnaire-specific token (shown above)</p>
                    </Segment>

                    <Header as="h5">Optional Body (JSON)</Header>
                    <Segment secondary>
                        <p><strong>startDate:</strong> Filter from date (format: YYYY-MM-DD, e.g. "2025-01-01")</p>
                        <p><strong>endDate:</strong> Filter to date (format: YYYY-MM-DD, e.g. "2025-12-31")</p>
                        <p style={{ color: '#666', fontSize: '12px', marginTop: '8px' }}>If omitted, all data will be exported.</p>
                    </Segment>

                    <Header as="h5">Example Request</Header>
                    <Segment secondary>
                        <pre style={{ overflow: 'auto', fontSize: '12px' }}>
                            {exportExample}
                        </pre>
                    </Segment>

                    <Header as="h5">Response (202 Accepted)</Header>
                    <Segment secondary>
                        <pre style={{ overflow: 'auto', fontSize: '12px' }}>
                            {exportResponse}
                        </pre>
                    </Segment>

                    <Divider />

                    <Header as="h4">2. Check Export Status</Header>
                    <Segment secondary>
                        <code>GET {statusEndpoint}</code>
                    </Segment>

                    <Header as="h5">Required Headers</Header>
                    <Segment secondary>
                        <p><strong>X-Tenant-Token:</strong> Your tenant's API token</p>
                        <p><strong>X-Questionnaire-Token:</strong> The questionnaire token</p>
                    </Segment>

                    <Header as="h5">Example Request</Header>
                    <Segment secondary>
                        <pre style={{ overflow: 'auto', fontSize: '12px' }}>
                            {statusExample}
                        </pre>
                    </Segment>

                    <Header as="h5">Response (When Completed)</Header>
                    <Segment secondary>
                        <pre style={{ overflow: 'auto', fontSize: '12px' }}>
                            {statusResponse}
                        </pre>
                    </Segment>

                    <Header as="h5">Possible Status Values</Header>
                    <Segment secondary>
                        <p><strong>Export Created:</strong> Export has been queued</p>
                        <p><strong>Export in Progress:</strong> Export is being processed</p>
                        <p><strong>Export Completed:</strong> Export is ready for download</p>
                        <p><strong>No data found to Export:</strong> No data available for this questionnaire</p>
                    </Segment>

                    <Divider />

                    <Header as="h4">3. Download CSV</Header>
                    <Message>
                        <p>When the status is "Export Completed", use the <code>downloadUrl</code> from the response to download the CSV file.</p>
                        <p><strong>Note:</strong> The download URL expires after 1 hour (3600 seconds). Request a new status check if the URL has expired.</p>
                    </Message>

                    <Divider />

                    <Header as="h5">Error Codes</Header>
                    <Segment secondary>
                        <p><strong>400:</strong> Missing required parameters</p>
                        <p><strong>401:</strong> Missing or invalid authentication tokens</p>
                        <p><strong>404:</strong> Export not found</p>
                        <p><strong>500:</strong> Internal server error</p>
                    </Segment>
                </>
            )}

            {!questionnaire?.externalApiEnabled && (
                <Message warning>
                    <Message.Header>API Access Disabled</Message.Header>
                    <p>Enable external API access above to view the API documentation and generate tokens.</p>
                </Message>
            )}
        </Segment>
    );
};

export default ApiDocumentationTab;
